import { useEffect, useRef, useState } from "react";
import "./AnimatedCursor.scss";

const CLICKABLE =
  'a, button, input, textarea, select, label[for], [role="button"], .link';

/**
 * Two-part custom cursor: a small dot that tracks the pointer exactly and
 * a soft ring that trails it and grows over interactive elements.
 *
 * Only enabled on devices with a fine pointer (mouse / trackpad) and when the
 * user has not requested reduced motion. Uses event delegation so it keeps
 * working as routes change.
 */
function CursorCore({ trailingSpeed = 8 }) {
  const dotRef = useRef(null);
  const ringRef = useRef(null);
  const target = useRef({ x: -100, y: -100 });
  const ring = useRef({ x: -100, y: -100 });

  useEffect(() => {
    const root = document.documentElement;
    root.classList.add("has-custom-cursor");
    let raf;

    const setState = (name, on) => {
      const el = ringRef.current;
      const dot = dotRef.current;
      if (!el || !dot) return;
      el.classList.toggle(name, on);
      dot.classList.toggle(name, on);
    };

    const onMove = (e) => {
      target.current = { x: e.clientX, y: e.clientY };
      if (dotRef.current) {
        dotRef.current.style.transform = `translate3d(${e.clientX}px, ${e.clientY}px, 0)`;
      }
      setState("is-visible", true);
    };
    const onOver = (e) => setState("is-hover", !!e.target.closest?.(CLICKABLE));
    const onDown = () => setState("is-down", true);
    const onUp = () => setState("is-down", false);
    const onLeave = () => setState("is-visible", false);

    const tick = () => {
      ring.current.x += (target.current.x - ring.current.x) / trailingSpeed;
      ring.current.y += (target.current.y - ring.current.y) / trailingSpeed;
      if (ringRef.current) {
        ringRef.current.style.transform = `translate3d(${ring.current.x}px, ${ring.current.y}px, 0)`;
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);

    document.addEventListener("mousemove", onMove, { passive: true });
    document.addEventListener("mouseover", onOver, { passive: true });
    document.addEventListener("mousedown", onDown);
    document.addEventListener("mouseup", onUp);
    document.documentElement.addEventListener("mouseleave", onLeave);

    return () => {
      cancelAnimationFrame(raf);
      root.classList.remove("has-custom-cursor");
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseover", onOver);
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("mouseup", onUp);
      document.documentElement.removeEventListener("mouseleave", onLeave);
    };
  }, [trailingSpeed]);

  return (
    <>
      <div ref={ringRef} className="cursor cursor--ring" aria-hidden="true">
        <span />
      </div>
      <div ref={dotRef} className="cursor cursor--dot" aria-hidden="true">
        <span />
      </div>
    </>
  );
}

const canUseCustomCursor = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(hover: hover) and (pointer: fine)").matches &&
  !window.matchMedia("(prefers-reduced-motion: reduce)").matches;

function AnimatedCursor(props) {
  const [enabled, setEnabled] = useState(false);

  useEffect(() => {
    setEnabled(canUseCustomCursor());
  }, []);

  return enabled ? <CursorCore {...props} /> : null;
}

export default AnimatedCursor;
